// src/pages/admin/AdminShared.jsx
import { useCallback, useEffect, useState } from "react";
import { api } from "@/lib/api";
import { toast } from "sonner";
import {
  LayoutDashboard, Users, Flag, LifeBuoy, BarChart3, Settings,
  Sparkles, Eye, UserCheck, MessageSquare, Bell, Shield,
  User as UserIcon, Calendar,
} from "lucide-react";

// =====================================================================
// Tab config
// =====================================================================
export const TABS = [
  { key: "overview",   label: "Overview",   icon: LayoutDashboard },
  { key: "users",      label: "Users",      icon: Users },
  { key: "moderation", label: "Moderation", icon: Flag },
  { key: "support",    label: "Support",    icon: LifeBuoy },
  { key: "analytics",  label: "Analytics",  icon: BarChart3 },
  { key: "system",     label: "System",     icon: Settings },
];

// =====================================================================
// Charts with Y-axis numbers
// =====================================================================
export function LineChart({ data, color = "#7C3AED", height = 100 }) {
  if (!data?.length) {
    return (
      <div className="flex items-center justify-center text-xs text-[#6B6B70]" style={{ height }}>
        No data available
      </div>
    );
  }
  const max = Math.max(1, ...data.map((d) => Number(d.cnt) || 0));
  const total = data.reduce((acc, d) => acc + (Number(d.cnt) || 0), 0);
  
  const points = data.map((d, i) => {
      const x = (i / Math.max(data.length - 1, 1)) * 100;
      const y = 100 - ((Number(d.cnt) || 0) / max) * 90 - 5;
      return `${x},${y}`;
    }).join(" ");

  return (
    <div className="relative w-full" style={{ height }}>
      <div className="flex justify-between items-center mb-1">
        <span className="text-[10px] font-bold text-gray-500 uppercase tracking-wide">Total: {total}</span>
        <span className="text-[10px] font-bold text-gray-500 uppercase tracking-wide">Peak: {max}</span>
      </div>
      <div className="relative h-full">
        <div className="absolute left-0 top-0 bottom-0 flex flex-col justify-between text-[9px] text-gray-400 w-6 text-right pr-2">
          <span>{max}</span>
          <span>{Math.round(max / 2)}</span>
          <span>0</span>
        </div>
        <div className="pl-6 h-full">
          <svg viewBox="0 0 100 100" preserveAspectRatio="none" style={{ width: "100%", height: "100%" }}>
            <line x1="0" y1="5" x2="100" y2="5" stroke="#F5F3EE" strokeWidth="1" vectorEffect="non-scaling-stroke" />
            <line x1="0" y1="50" x2="100" y2="50" stroke="#F5F3EE" strokeWidth="1" vectorEffect="non-scaling-stroke" />
            <line x1="0" y1="95" x2="100" y2="95" stroke="#F5F3EE" strokeWidth="1" vectorEffect="non-scaling-stroke" />
            <polyline points={points} fill="none" stroke={color} strokeWidth="1.5" vectorEffect="non-scaling-stroke" strokeLinejoin="round" strokeLinecap="round" />
          </svg>
        </div>
      </div>
    </div>
  );
}

export function BarChart({ data, color = "#7C3AED", height = 100 }) {
  if (!data?.length) {
    return (
      <div className="flex items-center justify-center text-xs text-[#6B6B70]" style={{ height }}>
        No data available
      </div>
    );
  }
  const max = Math.max(1, ...data.map((d) => Number(d.cnt) || 0));
  const total = data.reduce((acc, d) => acc + (Number(d.cnt) || 0), 0);
  const bw = 100 / data.length;

  return (
    <div className="relative w-full" style={{ height }}>
      <div className="flex justify-between items-center mb-1">
        <span className="text-[10px] font-bold text-gray-500 uppercase tracking-wide">Total: {total}</span>
        <span className="text-[10px] font-bold text-gray-500 uppercase tracking-wide">Peak: {max}</span>
      </div>
      <div className="relative h-full">
        <div className="absolute left-0 top-0 bottom-0 flex flex-col justify-between text-[9px] text-gray-400 w-6 text-right pr-2">
          <span>{max}</span>
          <span>{Math.round(max / 2)}</span>
          <span>0</span>
        </div>
        <div className="pl-6 h-full">
          <svg viewBox="0 0 100 100" preserveAspectRatio="none" style={{ width: "100%", height: "100%" }}>
            <line x1="0" y1="5" x2="100" y2="5" stroke="#F5F3EE" strokeWidth="1" vectorEffect="non-scaling-stroke" />
            <line x1="0" y1="50" x2="100" y2="50" stroke="#F5F3EE" strokeWidth="1" vectorEffect="non-scaling-stroke" />
            <line x1="0" y1="95" x2="100" y2="95" stroke="#F5F3EE" strokeWidth="1" vectorEffect="non-scaling-stroke" />
            {data.map((d, i) => {
              const h = ((Number(d.cnt) || 0) / max) * 95;
              return (
                <rect
                  key={i}
                  x={i * bw + bw * 0.15}
                  y={100 - h}
                  width={bw * 0.7}
                  height={h}
                  fill={color}
                  opacity={0.85}
                />
              );
            })}
          </svg>
        </div>
      </div>
    </div>
  );
}

export function DonutChart({ segments, size = 120 }) {
  const total = segments.reduce((a, s) => a + (s.value || 0), 0) || 1;
  let offset = 0;
  const r = 40;
  const c = 2 * Math.PI * r;
  return (
    <svg viewBox="0 0 100 100" style={{ width: size, height: size }}>
      <circle cx="50" cy="50" r={r} fill="none" stroke="#F5F3EE" strokeWidth="14" />
      {segments.map((s, i) => {
        const frac = (s.value || 0) / total;
        const dash = `${frac * c} ${c}`;
        const dashOff = -offset * c;
        offset += frac;
        return (
          <circle
            key={i}
            cx="50" cy="50" r={r}
            fill="none"
            stroke={s.color}
            strokeWidth="14"
            strokeDasharray={dash}
            strokeDashoffset={dashOff}
            transform="rotate(-90 50 50)"
          />
        );
      })}
    </svg>
  );
}

// =====================================================================
// Reusable cards
// =====================================================================
export function KpiCard({ label, value, icon: Icon, accent = "purple", sublabel }) {
  const accents = {
    purple: "text-purple-600 bg-purple-50",
    green: "text-emerald-600 bg-emerald-50",
    amber: "text-amber-600 bg-amber-50",
    blue: "text-blue-600 bg-blue-50",
    red: "text-red-600 bg-red-50",
  };
  return (
    <div className="bg-white border border-[#E7E5E0] rounded-2xl p-5">
      <div className="flex items-start justify-between gap-3">
        <div className="min-w-0 flex-1">
          <div className="text-xs text-[#6B6B70] uppercase tracking-wide font-medium">
            {label}
          </div>
          <div className="text-3xl font-bold mt-1 tabular-nums text-[#0F0F10]">
            {typeof value === "number" ? value.toLocaleString() : value}
          </div>
          {sublabel && (
            <div className="text-xs text-[#6B6B70] mt-0.5">{sublabel}</div>
          )}
        </div>
        <div className={`w-10 h-10 rounded-xl flex items-center justify-center flex-shrink-0 ${accents[accent]}`}>
          <Icon className="w-5 h-5" />
        </div>
      </div>
    </div>
  );
}

export function SectionCard({ title, subtitle, children, action }) {
  return (
    <div className="bg-white border border-[#E7E5E0] rounded-2xl p-5">
      <div className="flex items-start justify-between gap-3 mb-3">
        <div>
          <h3 className="font-semibold">{title}</h3>
          {subtitle && (
            <p className="text-xs text-[#6B6B70] mt-0.5">{subtitle}</p>
          )}
        </div>
        {action}
      </div>
      {children}
    </div>
  );
}

export function PendingBadge({ count }) {
  if (!count) return null;
  return (
    <span className="ml-1 px-1.5 py-0.5 rounded-full bg-red-500 text-white text-[10px] font-bold tabular-nums">
      {count > 99 ? "99+" : count}
    </span>
  );
}

export function timeAgo(d) {
  if (!d) return "Never";
  const s = Math.floor((Date.now() - new Date(d).getTime()) / 1000);
  if (s < 60) return "just now";
  if (s < 3600) return `${Math.floor(s / 60)}m ago`;
  if (s < 86400) return `${Math.floor(s / 3600)}h ago`;
  if (s < 2592000) return `${Math.floor(s / 86400)}d ago`;
  return new Date(d).toLocaleDateString();
}

export function fmtDate(d) {
  if (!d) return "—";
  return new Date(d).toLocaleDateString(undefined, {
    year: "numeric", month: "short", day: "numeric",
  });
}

// =====================================================================
// OverviewTab
// =====================================================================
export function OverviewTab({ onGoTo }) {
  const [data, setData] = useState(null);
  const [growth, setGrowth] = useState(null);
  const [loading, setLoading] = useState(true);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const [ov, gr] = await Promise.all([
        api.get("/admin/overview"),
        api.get("/admin/analytics/growth", { params: { days: 30 } }),
      ]);
      setData(ov.data);
      setGrowth(gr.data);
    } catch {
      toast.error("Failed to load overview");
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  if (loading) {
    return (
      <div className="text-center py-16">
        <div className="w-6 h-6 border-2 border-purple-600 border-t-transparent rounded-full animate-spin mx-auto" />
      </div>
    );
  }
  if (!data) return null;

  const { kpis, pending, platform_split, composition, recent_users } = data;

  const genderSegs = Object.entries(composition?.gender_split || {}).map(([k, v], i) => ({
    label: k,
    value: v,
    color: ["#7C3AED", "#EC4899", "#F59E0B", "#10B981", "#6B7280"][i % 5],
  }));

  const platformSegs = [
    { label: "Web", value: platform_split?.web || 0, color: "#3B82F6" },
    { label: "App", value: platform_split?.app || 0, color: "#10B981" },
  ];

  return (
    <div className="space-y-6">
      <div className="grid grid-cols-2 md:grid-cols-5 gap-4">
        <KpiCard label="Total users" value={kpis.total_users} icon={Users} accent="purple" />
        <KpiCard label="New today" value={kpis.new_today} icon={Calendar} accent="blue" />
        <KpiCard label="New this week" value={kpis.new_this_week} icon={Sparkles} accent="green" />
        <KpiCard label="Active (24h)" value={kpis.active_24h} icon={Eye} accent="amber" />
        <KpiCard
          label="Verified"
          value={composition?.verified ?? 0}
          icon={UserCheck}
          accent="purple"
          sublabel={`${Math.round(((composition?.verified || 0) / Math.max(kpis.total_users, 1)) * 100)}% of users`}
        />
      </div>

      <SectionCard title="Needs your attention" subtitle="Live pending items across all queues">
        <div className="grid grid-cols-2 md:grid-cols-5 gap-3">
          <button onClick={() => onGoTo("moderation", "reports")} className="text-left p-3 rounded-xl border border-[#E7E5E0] hover:bg-[#F5F3EE] transition-colors">
            <div className="flex items-center gap-2">
              <Flag className="w-4 h-4 text-red-500" />
              <span className="text-xs font-medium text-[#6B6B70]">Reports</span>
            </div>
            <div className="text-2xl font-bold mt-1 tabular-nums">{pending.reports}</div>
          </button>
          <button onClick={() => onGoTo("moderation", "shares")} className="text-left p-3 rounded-xl border border-[#E7E5E0] hover:bg-[#F5F3EE] transition-colors">
            <div className="flex items-center gap-2">
              <Sparkles className="w-4 h-4 text-pink-500" />
              <span className="text-xs font-medium text-[#6B6B70]">Share claims</span>
            </div>
            <div className="text-2xl font-bold mt-1 tabular-nums">{pending.share_claims}</div>
          </button>
          <button onClick={() => onGoTo("support", "tickets")} className="text-left p-3 rounded-xl border border-[#E7E5E0] hover:bg-[#F5F3EE] transition-colors">
            <div className="flex items-center gap-2">
              <MessageSquare className="w-4 h-4 text-indigo-500" />
              <span className="text-xs font-medium text-[#6B6B70]">Tickets</span>
            </div>
            <div className="text-2xl font-bold mt-1 tabular-nums">{pending.support_tickets}</div>
          </button>
          <button onClick={() => onGoTo("support", "feedback")} className="text-left p-3 rounded-xl border border-[#E7E5E0] hover:bg-[#F5F3EE] transition-colors">
            <div className="flex items-center gap-2">
              <Bell className="w-4 h-4 text-amber-500" />
              <span className="text-xs font-medium text-[#6B6B70]">Feedback</span>
            </div>
            <div className="text-2xl font-bold mt-1 tabular-nums">{pending.feedback}</div>
          </button>
          <button onClick={() => onGoTo("support", "dsr")} className="text-left p-3 rounded-xl border border-[#E7E5E0] hover:bg-[#F5F3EE] transition-colors">
            <div className="flex items-center gap-2">
              <Shield className="w-4 h-4 text-emerald-500" />
              <span className="text-xs font-medium text-[#6B6B70]">DSR</span>
            </div>
            <div className="text-2xl font-bold mt-1 tabular-nums">{pending.dsr_requests}</div>
          </button>
        </div>
      </SectionCard>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
        <div className="lg:col-span-2">
          <SectionCard
            title="Signups — last 30 days"
            action={
              <button onClick={() => onGoTo("analytics", "growth")} className="text-xs text-purple-600 hover:underline">
                Open analytics →
              </button>
            }
          >
            <LineChart data={growth?.signups || []} height={160} />
          </SectionCard>
        </div>
        
        {/* Platform and Gender split column */}
        <div className="space-y-4">
          <SectionCard title="Platform split">
            {platformSegs[0].value === 0 && platformSegs[1].value === 0 ? (
              <div className="text-xs text-[#6B6B70]">No platform data yet</div>
            ) : (
              <div className="flex items-center gap-4">
                <DonutChart segments={platformSegs} size={90} />
                <div className="space-y-2 text-xs">
                  {platformSegs.map((s) => (
                    <div key={s.label} className="flex items-center gap-2">
                      <span
                        className="w-2.5 h-2.5 rounded-full flex-shrink-0"
                        style={{ backgroundColor: s.color }}
                      />
                      <span className="text-[#6B6B70]">{s.label}</span>
                      <span className="font-semibold tabular-nums">{s.value}</span>
                    </div>
                  ))}
                </div>
              </div>
            )}
          </SectionCard>

          <SectionCard title="Gender split">
            {genderSegs.length === 0 ? (
              <div className="text-xs text-[#6B6B70]">No profile data yet</div>
            ) : (
              <div className="flex items-center gap-4">
                <DonutChart segments={genderSegs} size={90} />
                <div className="space-y-1 text-xs">
                  {genderSegs.map((s) => (
                    <div key={s.label} className="flex items-center gap-2">
                      <span
                        className="w-2 h-2 rounded-full flex-shrink-0"
                        style={{ backgroundColor: s.color }}
                      />
                      <span className="capitalize text-[#6B6B70]">{s.label}</span>
                      <span className="font-semibold tabular-nums">{s.value}</span>
                    </div>
                  ))}
                </div>
              </div>
            )}
          </SectionCard>
        </div>
      </div>

      <SectionCard title="Recent signups" action={<button onClick={() => onGoTo("users")} className="text-xs text-purple-600 hover:underline">All users →</button>}>
        <div className="divide-y divide-[#F5F3EE]">
          {recent_users.map((u) => (
            <div key={u.user_id} className="flex items-center gap-3 py-2">
              <div className="w-8 h-8 rounded-full overflow-hidden bg-[#F5F3EE] flex-shrink-0">
                {u.picture ? (
                  <img src={u.picture} alt="" className="w-full h-full object-cover" />
                ) : (
                  <div className="w-full h-full grid place-items-center text-gray-400">
                    <UserIcon className="w-3.5 h-3.5" />
                  </div>
                )}
              </div>
              <div className="flex-1 min-w-0">
                <div className="text-sm font-medium truncate">{u.name || u.email}</div>
                <div className="text-xs text-[#6B6B70] truncate">{u.email}</div>
              </div>
              <div className="text-xs text-[#6B6B70] flex-shrink-0">{timeAgo(u.created_at)}</div>
            </div>
          ))}
        </div>
      </SectionCard>
    </div>
  );
}